// Per-user GolfNow facility-id overrides.
//
// A user who sees wrong/absent live tee times can paste a GolfNow link; we
// store the extracted facility id as a personal suggestion (never touching the
// shared golf_clubs row). Their own results then use the suggested id until an
// admin reviews it. Writes go through the submit_facility_override RPC; reads
// are RLS-scoped to the caller's own rows.

import { supabase } from '../lib/supabase';

/**
 * Submit (or replace) the current user's facility-id override for a club.
 * @param {string} golfClubId - golf_clubs.id (UUID)
 * @param {number} facilityId - GolfNow facility id extracted from the pasted URL
 * @param {string|null} sourceUrl - The pasted GolfNow URL, for admin review
 * @returns {Promise<{ success: boolean, id?: string, facility_id?: number, error?: string }>}
 */
export async function submitFacilityOverride(golfClubId, facilityId, sourceUrl = null) {
  const { data, error } = await supabase.rpc('submit_facility_override', {
    p_golf_club_id: golfClubId,
    p_facility_id: facilityId,
    p_source_url: sourceUrl,
  });

  if (error) {
    throw new Error(error.message || 'Could not save your tee-time link');
  }
  if (data && data.success === false) {
    throw new Error(data.error || 'Could not save your tee-time link');
  }
  return data;
}

/**
 * Get the current user's override for a club, if any.
 * @param {string} golfClubId - golf_clubs.id (UUID)
 * @returns {Promise<object|null>} The override row, or null when none exists
 */
export async function getMyFacilityOverride(golfClubId) {
  if (!golfClubId) return null;

  const { data, error } = await supabase
    .from('user_golfnow_facility_overrides')
    .select('id, golf_club_id, suggested_facility_id, source_url, status, created_at')
    .eq('golf_club_id', golfClubId)
    .maybeSingle();

  if (error) {
    console.warn('[overridesService] getMyFacilityOverride failed:', error.message);
    return null;
  }
  return data;
}
